import { lazy, type ComponentType } from 'react';
import type { SlidePreview } from '@/features/onboarding/types';

function lazyPreview(load: () => Promise<ComponentType>): SlidePreview {
  // One promise for both the preload and React.lazy, so a slide renders from the cache
  // instead of starting a second download.
  let pending: Promise<{ default: ComponentType }> | undefined;
  const loadDefault = () => {
    pending ??= load()
      .then(Component => ({ default: Component }))
      .catch(error => {
        pending = undefined;
        throw error;
      });
    return pending;
  };
  return {
    Component: lazy(loadDefault),
    // A failed download surfaces again when the slide renders the component.
    preload: () => {
      loadDefault().catch(() => {});
    },
  };
}

export const bookingPreview = lazyPreview(() =>
  import('./BookingPreview').then(m => m.BookingPreview)
);

export const floorsPreview = lazyPreview(() =>
  import('./FloorsPreview').then(m => m.FloorsPreview)
);

export const eventsPreview = lazyPreview(() =>
  import('./EventsPreview').then(m => m.EventsPreview)
);

export const suppliesAndSuggestionsPreview = lazyPreview(() =>
  import('./SuppliesAndSuggestionsPreview').then(m => m.SuppliesAndSuggestionsPreview)
);

export const profilePreview = lazyPreview(() =>
  import('./ProfilePreview').then(m => m.ProfilePreview)
);

export const statsPreview = lazyPreview(() => import('./StatsPreview').then(m => m.StatsPreview));

export const assistantChatPreview = lazyPreview(() =>
  import('./AssistantChatPreview').then(m => m.AssistantChatPreview)
);

export const officeBuilderPreview = lazyPreview(() =>
  import('./OfficeBuilderPreview').then(m => m.OfficeBuilderPreview)
);
